<script lang="typescript">
    import IconRow from "./IconRow.svelte";
    import Logo from "./Logo.svelte";
    import FocalTypeModal from "./FocalTypeModal.svelte";
    import PromoMessage from "./PromoMessage.svelte";
    import { setState } from "./stores";
    import { MessageType, FocalType, EyeWearType, Promotion } from "./types";
    import { onMount } from "svelte";
    import { getRegion } from "./utils";
    import { push } from "svelte-spa-router";
    import { fade, fly, scale } from "svelte/transition";

    onMount(() =>
        setState({
            message: MessageType.RESET,
            region: getRegion(window.location.pathname),
        })
    );

    const clickHandler = (focalType: FocalType) => {
        setState({ message: MessageType.DISMISS_SPLASH_SCREEN, focalType });
        push("/prescription/");
    };

    const contactLensClickHandler = () => {
        setState({ message: MessageType.CONTACT_LENSES_SELECTED });
        push("/contact-lenses/");
    };

    let activeFocalType = null;
    let activeEyeWearType = null;
    let hidePromo = false;

    function getMouseOver(focalType: FocalType): () => void {
        return () => {
            activeFocalType = focalType;
            hidePromo = true;
        };
    }

    function getContactLensMouseOver(eyeWearType: EyeWearType): () => void {
        return () => {
            activeEyeWearType = eyeWearType;
            hidePromo = true;
        };
    }

    function mouseOut(): void {
        activeFocalType = null;
        activeEyeWearType = null;
        hidePromo = false;
    }
</script>

<section class="justify-center h-screen bg-primary" data-testid="splash-section">
    <div
        class="relative flex-col items-start page-container max-w-none md:flex-row"
        in:fade={{ duration: 700 }}
    >
        <div
            class="relative flex flex-col justify-between w-full h-full px-12 mb-auto md:w-2/3 md:h-screen"
        />
        <div class="relative img-container">
            <!-- <Curve /> -->
            <div class="absolute top-0 left-0 w-full h-full ">
                <IconRow {hidePromo} />
                <FocalTypeModal {activeFocalType} {activeEyeWearType} />
            </div>
        </div>
        <div class="absolute z-30 w-full h-full">
            <div class="w-1/2 px-4 mb-8">
                <Logo classNames="mt-8 md:mt-12 mr-2 md:mr-8 w-auto" />
            </div>
            <div class="w-2/3 mb-12 ml-4">
                <h1 data-testid="splash-h1" class="mt-8">Help me find my perfect…</h1>
            </div>
            <div class="absolute flex flex-col items-start justify-center w-2/3 ml-12 space-y-2">
                <button
                    class="btn-secondary btn-lg"
                    on:click={() => clickHandler(FocalType.SINGLE_VISION)}
                    in:fly={{ y: 200, duration: 600 }}
                    on:mouseover={getMouseOver(FocalType.SINGLE_VISION)}
                    on:mouseout={mouseOut}
                >
                    Single vision lenses
                </button>

                <button
                    class="btn-secondary btn-lg"
                    on:click={() => clickHandler(FocalType.VARIFOCAL)}
                    in:fly={{ y: 200, duration: 600, delay: 200 }}
                    on:mouseover={getMouseOver(FocalType.VARIFOCAL)}
                    on:mouseout={mouseOut}
                >
                    Varifocal lenses
                </button>

                <button
                    class="btn-secondary btn-lg"
                    on:click={() => clickHandler(FocalType.CHILDREN_SINGLE_VISION)}
                    in:fly={{ y: 200, duration: 600, delay: 200 }}
                    on:mouseover={getMouseOver(FocalType.CHILDREN_SINGLE_VISION)}
                    on:mouseout={mouseOut}
                >
                    Children's lenses
                </button>

                {#if CONTACT_LENS}
                    <button
                        class="btn-secondary btn-lg"
                        on:click={() => contactLensClickHandler()}
                        in:fly={{ y: 200, duration: 600, delay: 200 }}
                        on:mouseover={getContactLensMouseOver(EyeWearType.CONTACT_LENS)}
                        on:mouseout={mouseOut}
                    >
                        <!-- TODO: update the getMouseOver for contact lenses -->
                        Contact lenses
                    </button>
                {/if}
            </div>
        </div>
    </div>
</section>

<style>
    .img-container {
        @apply w-full order-first md:order-last md:w-1/2 bg-no-repeat bg-cover bg-splash;
        @apply bg-center lg:bg-right;
        @apply md:h-screen;
    }

    .btn-lg {
        @apply text-xl md:text-3xl tracking-tight pt-4 w-2/3;
    }

    h1 {
        @apply text-white text-4xl md:text-5xl font-medium md:w-full;
        @apply mx-auto md:ml-8 md:mr-0 mt-16 md:mt-auto p-0 leading-tight tracking-tight;
    }
</style>
